import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CircleHelp,
  Copy,
  ExternalLink,
  FileText,
  Mail,
  MapPin,
  Package,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  ShoppingBasket,
  Truck,
  UserRound,
  XCircle,
} from 'lucide-react';
import { Breadcrumbs, Card, EmptyState, Status } from '../components/UI';
import { api, dateTime, emailAddress, money } from '../lib/api';

const text=value=>String(value??'').trim();
const lower=value=>text(value).toLowerCase();
const available=value=>lower(value?.isAvailable)==='yes';
const parse=value=>{try{return value?JSON.parse(value):null;}catch{return null;}};

function DetailList({children}){return <dl className="detail-list order-detail-list">{children}</dl>;}
function Detail({label,children}){return <div><dt>{label}</dt><dd>{children??'—'}</dd></div>;}
function StatusFact({label,value}){return <div className="order-status-fact"><span>{label}</span><Status value={value||'unknown'}/></div>;}

export default function OrderDetailPage(){
  const{id}=useParams();
  const navigate=useNavigate();
  const[data,setData]=useState(null);
  const[loading,setLoading]=useState(true);
  const[error,setError]=useState('');
  const[notice,setNotice]=useState('');
  const[busy,setBusy]=useState('');
  const[editor,setEditor]=useState('');
  const[shippingMethod,setShippingMethod]=useState('Budget');
  const[recipient,setRecipient]=useState({name:'',email:'',phoneNumber:'',address:{line1:'',line2:'',townOrCity:'',stateOrCounty:'',postalOrZipCode:'',countryCode:'GB'}});
  const[metadata,setMetadata]=useState('{}');

  const load=useCallback(async()=>{
    setLoading(true);setError('');
    try{
      const next=await api('/api/shop-orders/'+encodeURIComponent(id));
      setData(next);
      const providerRecipient=next?.provider?.order?.recipient;
      const localRecipient=parse(next?.order?.shipping_address_json);
      const r=providerRecipient?.address?providerRecipient:localRecipient?.address?localRecipient:{
        name:next?.order?.recipient_name||'',
        email:next?.order?.recipient_email||next?.order?.guest_email||'',
        phoneNumber:next?.order?.recipient_phone||'',
        address:{line1:'',line2:'',townOrCity:'',stateOrCounty:'',postalOrZipCode:'',countryCode:next?.order?.destination_country||'GB'},
      };
      setRecipient({
        name:r?.name||'',email:r?.email||'',phoneNumber:r?.phoneNumber||'',
        address:{
          line1:r?.address?.line1||'',line2:r?.address?.line2||'',townOrCity:r?.address?.townOrCity||'',
          stateOrCounty:r?.address?.stateOrCounty||'',postalOrZipCode:r?.address?.postalOrZipCode||'',countryCode:r?.address?.countryCode||'GB',
        },
      });
      setShippingMethod(next?.provider?.order?.shippingMethod||next?.order?.shipping_method||next?.quote?.shipping_method||'Budget');
      setMetadata(JSON.stringify(next?.provider?.order?.metadata||{},null,2));
    }catch(e){setError(e.message);}
    finally{setLoading(false);}
  },[id]);

  useEffect(()=>{void load();},[load]);

  const recordType=data?.recordType||'';
  const isQuote=recordType==='quote'||recordType==='quote_legacy';
  const record=isQuote?(data?.quote||data?.order):data?.order;
  const paymentProvider=lower(record?.payment_provider||(record?.paypal_order_id?'paypal':'stripe'))||'stripe';
  const isPayPalPayment=paymentProvider==='paypal';
  const reference=record?.quote_reference||record?.order_reference||record?.provider_order_reference||record?.id||'Order';
  const customerId=record?.customer_id||'';
  const providerOrder=data?.provider?.order||null;
  let cjOrder=null;
  try{cjOrder=record?.cj_order_json?JSON.parse(record.cj_order_json):null;}catch{cjOrder=null;}
  const actions=data?.provider?.actions||null;
  const canCancel=available(actions?.cancel);
  const canRetry=!isQuote&&['paid_awaiting_fulfilment','fulfilment_retry'].includes(lower(record?.status));
  const canRecipient=available(actions?.changeRecipientDetails)||canRetry;
  const canShipping=available(actions?.changeShippingMethod)||canRetry;
  const canMetadata=available(actions?.changeMetaData)||Boolean(record?.prodigi_order_id);
  const items=data?.items||[];
  const events=data?.events||[];
  const docs=data?.documents||{};
  const customerEmail=record?.customer_email||record?.recipient_email||record?.guest_email||record?.customer_verified_email||'';
  const quotePayload=parse(record?.payload_json)||{};
  const contract=isQuote
    ? (quotePayload?.contract||{})
    : {
        termsVersion:record?.terms_version||'',
        termsAcceptedAt:record?.terms_accepted_at||'',
        personalisedCancellationNoticeAcknowledged:Number(record?.personalised_terms_acknowledged||0)===1,
      };

  const run=async(action,payload={},message='Updated.')=>{
    setBusy(action);setNotice('');
    try{
      const next=await api('/api/shop-orders/'+encodeURIComponent(record.id)+'/actions/'+action,{
        method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(payload),
      });
      setData(next);setEditor('');setNotice(message);await load();
    }catch(e){setNotice('Error: '+e.message);}
    finally{setBusy('');}
  };

  const documentAction=async(action,message)=>{
    setBusy(action);setNotice('');
    try{
      await api('/api/shop-orders/'+encodeURIComponent(record.id)+'/documents/'+action,{method:'POST',headers:{'content-type':'application/json'},body:'{}'});
      setNotice(message);await load();
    }catch(e){setNotice('Error: '+e.message);}
    finally{setBusy('');}
  };

  const saveMetadata=()=>{
    let value;
    try{value=JSON.parse(metadata||'{}');}catch{return setNotice('Error: Metadata must be valid JSON.');}
    void run('metadata',{metadata:value},'Prodigi metadata updated.');
  };

  const copy=value=>navigator.clipboard?.writeText(text(value)).then(()=>setNotice('Copied to clipboard.')).catch(()=>setNotice('Error: Could not copy to clipboard.'));

  const progress=useMemo(()=>providerOrder?.status?.details?Object.entries(providerOrder.status.details):[],[providerOrder]);

  if(loading&&!data)return <EmptyState title="Loading order" body="Reading Customer Operations, payment and fulfilment records…"/>;
  if(error||!record)return <><Breadcrumbs items={['Orders','Order']}/><EmptyState title="Order unavailable" body={error||'The order or quote could not be loaded.'}/></>;

  return <>
    <Breadcrumbs items={['Commercial','Orders',reference]}/>
    <section className="order-detail-hero">
      <div className="order-detail-title">
        <button className="icon-button" onClick={()=>navigate('/orders')} aria-label="Back to orders"><ArrowLeft size={18}/></button>
        <span className={isQuote?'order-detail-icon quote':'order-detail-icon'}>{isQuote?<ShoppingBasket size={22}/>:<Package size={22}/>}</span>
        <div>
          <div className="record-kicker">{isQuote?'Sousa Murray Shop quote / basket':'Sousa Murray Shop order'}</div>
          <h1 className="govuk-heading-xl">{reference}</h1>
          <div className="record-meta">
            <Status value={recordType==='quote_legacy'?'legacy quote':isQuote?(record.status==='checkout_open'?'quote open':record.status||'quote'):(record.status||'unknown')}/>
            {!isQuote&&record.prodigi_order_id&&<Status value={record.prodigi_status||'Prodigi submitted'}/>}
            {!isQuote&&record.cj_order_id&&<Status value={record.cj_status||'CJ submitted'}/>} 
            <span>{record.checkout_mode==='guest'?'Guest checkout':'My Sousa Murray checkout'}</span>
            <span>{isPayPalPayment?'PayPal':'Stripe'} payment</span>
          </div>
        </div>
      </div>
      <div className="record-actions">
        <button className="button secondary govuk-button govuk-button--secondary" disabled={Boolean(busy)} onClick={()=>void run('sync',{},'Live status refreshed.')}><RefreshCw size={15}/>Refresh</button>
        {customerId&&<button className="button secondary govuk-button govuk-button--secondary" onClick={()=>navigate('/customers/'+encodeURIComponent(customerId))}><UserRound size={15}/>Customer360</button>}
        {customerEmail&&<a className="button secondary govuk-button govuk-button--secondary" href={'mailto:'+customerEmail}><Mail size={15}/>Email customer</a>}
      </div>
    </section>

    {notice&&<div className={'operation-notice '+(notice.startsWith('Error:')?'error':'')}>{notice}</div>}

    {isQuote&&<div className="order-quote-banner">
      <ShoppingBasket size={20}/>
      <div><strong>{recordType==='quote_legacy'?'Legacy pre-payment checkout':'Quote — no order has been submitted'}</strong><span>{recordType==='quote_legacy'?'This is an older checkout record created before the quote lifecycle. New checkouts remain quotes until payment is completed.':'Customer Operations keeps this as a quote/basket until payment completes. It is not sent to Prodigi and is not treated as an order.'}</span></div>
      {recordType==='quote'&&<div className="order-quote-actions">{record.checkout_url&&<a className="button primary small govuk-button" href={record.checkout_url} target="_blank" rel="noreferrer"><ExternalLink size={14}/>Open checkout</a>}<button className="button secondary small govuk-button govuk-button--secondary" disabled={Boolean(busy)} onClick={()=>void run('resend-basket',{},'Basket email sent.')}><Send size={14}/>Send basket</button><button className="button danger-outline small govuk-button govuk-button--secondary" disabled={Boolean(busy)} onClick={()=>confirm('Cancel this quote and stop its basket reminders?')&&void run('cancel-quote',{},'Quote cancelled.')}><XCircle size={14}/>Cancel quote</button></div>}
    </div>}

    {!isQuote&&<section className="order-primary-actions">
      <div>
        <h2 className="govuk-heading-m">Order actions</h2>
        <p className="govuk-body">Actions are enabled only where the current payment and supplier fulfilment state permits them.</p>
      </div>
      <div>
        {canRetry&&<button className="button primary govuk-button" disabled={Boolean(busy)} onClick={()=>void run('retry-fulfilment',{},'Fulfilment submission retried.')}><Truck size={15}/>Submit / retry fulfilment</button>}
        {(record.prodigi_order_id||canRetry)&&<button className="button secondary govuk-button govuk-button--secondary" disabled={!canShipping||Boolean(busy)} onClick={()=>setEditor(editor==='shipping'?'':'shipping')}><Truck size={15}/>Change shipping</button>}
        {(record.prodigi_order_id||canRetry)&&<button className="button secondary govuk-button govuk-button--secondary" disabled={!canRecipient||Boolean(busy)} onClick={()=>setEditor(editor==='recipient'?'':'recipient')}><MapPin size={15}/>Amend recipient</button>}
        {record.prodigi_order_id&&<button className="button secondary govuk-button govuk-button--secondary" disabled={!canMetadata||Boolean(busy)} onClick={()=>setEditor(editor==='metadata'?'':'metadata')}><Save size={15}/>Metadata</button>}
        {record.prodigi_order_id&&<button className="button danger-outline govuk-button govuk-button--secondary" disabled={!canCancel||Boolean(busy)} onClick={()=>confirm('Cancel this order with Prodigi? This may stop production and cannot be undone from Customer Operations.')&&void run('cancel',{},'Cancellation request sent to Prodigi.')}><XCircle size={15}/>Cancel order</button>}
      </div>
    </section>}

    {editor==='shipping'&&<section className="order-editor">
      <label className="govuk-label"><span>Shipping method</span><select className="govuk-select" value={shippingMethod} onChange={e=>setShippingMethod(e.target.value)}><option>Budget</option><option>Standard</option><option>StandardPlus</option><option>Express</option><option>Overnight</option></select></label>
      <button className="button primary govuk-button" disabled={Boolean(busy)} onClick={()=>void run('shipping',{shippingMethod},'Shipping method updated with Prodigi.')}><Save size={15}/>Save shipping</button>
    </section>}

    {editor==='recipient'&&<section className="order-editor order-recipient-editor">
      <label className="govuk-label"><span>Name</span><input className="govuk-input" value={recipient.name} onChange={e=>setRecipient(v=>({...v,name:e.target.value}))}/></label>
      <label className="govuk-label"><span>Email</span><input className="govuk-input" type="email" value={recipient.email} onChange={e=>setRecipient(v=>({...v,email:e.target.value}))}/></label>
      <label className="govuk-label"><span>Telephone</span><input className="govuk-input" value={recipient.phoneNumber} onChange={e=>setRecipient(v=>({...v,phoneNumber:e.target.value}))}/></label>
      <label className="govuk-label"><span>Address line 1</span><input className="govuk-input" value={recipient.address.line1} onChange={e=>setRecipient(v=>({...v,address:{...v.address,line1:e.target.value}}))}/></label>
      <label className="govuk-label"><span>Address line 2</span><input className="govuk-input" value={recipient.address.line2} onChange={e=>setRecipient(v=>({...v,address:{...v.address,line2:e.target.value}}))}/></label>
      <label className="govuk-label"><span>Town / city</span><input className="govuk-input" value={recipient.address.townOrCity} onChange={e=>setRecipient(v=>({...v,address:{...v.address,townOrCity:e.target.value}}))}/></label>
      <label className="govuk-label"><span>County / region</span><input className="govuk-input" value={recipient.address.stateOrCounty} onChange={e=>setRecipient(v=>({...v,address:{...v.address,stateOrCounty:e.target.value}}))}/></label>
      <label className="govuk-label"><span>Postcode</span><input className="govuk-input" value={recipient.address.postalOrZipCode} onChange={e=>setRecipient(v=>({...v,address:{...v.address,postalOrZipCode:e.target.value}}))}/></label>
      <label className="govuk-label"><span>Country code</span><input className="govuk-input" value={recipient.address.countryCode} onChange={e=>setRecipient(v=>({...v,address:{...v.address,countryCode:e.target.value.toUpperCase()}}))}/></label>
      <div className="order-editor-actions"><button className="button primary govuk-button" disabled={Boolean(busy)} onClick={()=>void run('recipient',{recipient},'Recipient details updated with Prodigi.')}><Save size={15}/>Save recipient</button></div>
    </section>}

    {editor==='metadata'&&<section className="order-editor">
      <label className="order-metadata-field govuk-label"><span>Prodigi metadata — saving replaces the existing metadata</span><textarea className="govuk-textarea" rows={9} maxLength={2000} value={metadata} onChange={e=>setMetadata(e.target.value)} spellCheck="false"/></label>
      <button className="button primary govuk-button" disabled={Boolean(busy)} onClick={saveMetadata}><Save size={15}/>Save metadata</button>
    </section>}

    <div className="order-detail-grid">
      <Card title={isQuote?'Quote summary':'Order summary'}>
        <DetailList>
          <Detail label="Reference">{reference}</Detail>
          <Detail label="Created">{dateTime(record.created_at)}</Detail>
          <Detail label="Total">{money(record.total_minor||0,record.currency||'GBP')}</Detail>
          <Detail label="Products">{money(record.merchandise_minor||0,record.currency||'GBP')}</Detail>
          <Detail label="Delivery">{money(record.shipping_minor||0,record.currency||'GBP')}</Detail>
          <Detail label="Shipping method">{record.shipping_method||'—'}</Detail>
          {Array.isArray(data?.deliveryBreakdown)&&data.deliveryBreakdown.map((delivery,index)=><Detail key={delivery.key||index} label={delivery.provider==='cj'?'CJ · Everyday delivery':delivery.provider==='prodigi'?'Prodigi · Personalised delivery':'Delivery group'}>{money(delivery.amountMinor||0,record.currency||'GBP')}{delivery.method?' · '+delivery.method:''}</Detail>)}
          {isQuote&&<Detail label="Expires">{dateTime(record.expires_at)}</Detail>}
          {isQuote&&<Detail label="Basket reminders">{Number(record.reminder_count||0)}</Detail>}
          {isQuote&&<Detail label="Next reminder">{dateTime(record.next_reminder_at)}</Detail>}
        </DetailList>
      </Card>

      <Card title="Customer & contact">
        <DetailList>
          <Detail label="Customer">{record.customer_name||record.customer_number||'Guest / not yet linked'}</Detail>
          <Detail label="Customer number">{record.customer_number||'—'}</Detail>
          <Detail label="Email">{emailAddress(customerEmail)}</Detail>
          <Detail label="Recipient">{record.recipient_name||providerOrder?.recipient?.name||'—'}</Detail>
          <Detail label="Telephone">{record.recipient_phone||providerOrder?.recipient?.phoneNumber||'—'}</Detail>
          <Detail label="Checkout">{record.checkout_mode==='guest'?'Guest':'My Sousa Murray'}</Detail>
        </DetailList>
      </Card>

      <Card title="Shop contract">
        <DetailList>
          <Detail label="Terms version">{contract?.termsVersion||'—'}</Detail>
          <Detail label="Accepted">{contract?.termsAcceptedAt?dateTime(contract.termsAcceptedAt):'—'}</Detail>
          <Detail label="Personalised-goods notice">{contract?.personalisedCancellationNoticeAcknowledged===true?'Acknowledged':'Not applicable / not acknowledged'}</Detail>
          <Detail label="Terms"><a href="https://sousamurray.com/legal/shop" target="_blank" rel="noreferrer">Open Shop Terms <ExternalLink size={12}/></a></Detail>
        </DetailList>
      </Card>

      {!isQuote&&<Card title="Payment & documents">
        <DetailList>
          <Detail label="Paid">{record.paid_at?dateTime(record.paid_at):'Not recorded as paid'}</Detail>
          <Detail label="Provider">{isPayPalPayment?'PayPal':'Stripe'}</Detail>
          {isPayPalPayment
            ? <>
                <Detail label="PayPal order">{record.paypal_order_id||'—'}</Detail>
                <Detail label="Capture">{record.paypal_capture_id||'—'}</Detail>
                <Detail label="Payer email">{emailAddress(record.paypal_payer_email||customerEmail)||'—'}</Detail>
              </>
            : <>
                <Detail label="Stripe customer">{record.stripe_customer_id||'—'}</Detail>
                <Detail label="Payment intent">{record.stripe_payment_intent_id||'—'}</Detail>
                <Detail label="Invoice">{docs.invoice?.reference||record.stripe_invoice_id||'—'}</Detail>
                <Detail label="Receipt">{docs.receipt?.reference||record.stripe_charge_id||'—'}</Detail>
              </>}
        </DetailList>
        <div className="order-card-actions">
          {isPayPalPayment
            ? <button className="button secondary small govuk-button govuk-button--secondary" onClick={()=>navigate('/paypal-operations'+(customerId?'?customer='+encodeURIComponent(customerId):''))}><ExternalLink size={14}/>PayPal Operations</button>
            : <>
                {docs.invoice?.hostedInvoiceUrl&&<a className="button secondary small govuk-button govuk-button--secondary" href={docs.invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer"><ExternalLink size={14}/>View invoice</a>}
                {docs.invoice?.invoicePdf&&<a className="button secondary small govuk-button govuk-button--secondary" href={docs.invoice.invoicePdf} target="_blank" rel="noreferrer"><FileText size={14}/>PDF</a>}
                {docs.invoice?.id&&<button className="button secondary small govuk-button govuk-button--secondary" disabled={Boolean(busy)} onClick={()=>void documentAction('resend-invoice','Invoice re-sent to the customer.')}><Send size={14}/>Re-send invoice</button>}
                {docs.receipt?.receiptUrl&&<a className="button secondary small govuk-button govuk-button--secondary" href={docs.receipt.receiptUrl} target="_blank" rel="noreferrer"><ExternalLink size={14}/>View receipt</a>}
                {docs.receipt?.id&&<button className="button secondary small govuk-button govuk-button--secondary" disabled={Boolean(busy)} onClick={()=>void documentAction('resend-receipt','Stripe receipt re-sent to the customer.')}><Send size={14}/>Re-send receipt</button>}
              </>}
        </div>
        {!isPayPalPayment&&docs.warning&&<div className="operation-notice error">{docs.warning}</div>}
      </Card>}

      {!isQuote&&<Card title="Fulfilment">
        <div className="order-status-grid">
          <StatusFact label="CRM order" value={record.status}/>
          <StatusFact label="Prodigi" value={record.prodigi_status||providerOrder?.status?.stage||(items.some(item=>lower(item.fulfilment_provider||'prodigi')==='prodigi')?'not submitted':'not required')}/>
          {(record.cj_order_id||items.some(item=>lower(item.fulfilment_provider)==='cj'))&&<StatusFact label="Legacy CJ" value={record.cj_status||'not submitted'}/>} 
        </div>
        <DetailList>
          <Detail label="Prodigi order">{record.prodigi_order_id||'Not submitted'}</Detail>
          {record.cj_order_id&&<Detail label="Legacy CJ order">{record.cj_order_id}</Detail>}
          <Detail label="Submitted">{dateTime(record.submitted_for_fulfilment_at)}</Detail>
          <Detail label="Last provider sync">{dateTime(record.last_provider_sync_at)}</Detail>
          <Detail label="Fulfilled">{dateTime(record.fulfilled_at)}</Detail>
          <Detail label="Fulfilment error">{record.fulfilment_error||'—'}</Detail>
        </DetailList>
      </Card>}
    </div>

    {!isQuote&&progress.length>0&&<section className="order-section">
      <div className="order-section-head"><div><span>PRODIGI</span><h2 className="govuk-heading-m">Fulfilment progress</h2></div></div>
      <div className="order-progress-grid">{progress.map(([key,value])=><div key={key}><span>{key.replaceAll(/([A-Z])/g,' $1')}</span><Status value={value}/></div>)}</div>
      {providerOrder?.status?.issues?.length?<div className="order-issues">{providerOrder.status.issues.map((issue,index)=><div key={issue.objectId||index}><strong>{issue.errorCode||'Provider issue'}</strong><span>{issue.description||'No description supplied.'}</span></div>)}</div>:null}
    </section>}

    <section className="order-section">
      <div className="order-section-head"><div><span>ITEMS</span><h2 className="govuk-heading-m">Products & artwork</h2></div><strong>{items.length} line{items.length===1?'':'s'}</strong></div>
      <div className="order-items-table">{items.map(item=><article key={item.id}>
        <div><span className="shop-order-icon"><Package size={17}/></span><span><strong>{item.title}</strong><small>{item.product_code||item.supplier_sku}</small></span></div>
        <div><span>SKU</span><strong>{item.supplier_sku}</strong></div>
        <div><span>Provider</span><strong>{lower(item.fulfilment_provider||'prodigi')==='cj'?'CJ · Everyday':'Prodigi'}</strong></div>
        <div><span>Qty</span><strong>{item.quantity}</strong></div>
        <div><span>Line total</span><strong>{money(item.line_total_minor||0,record.currency||'GBP')}</strong></div>
        <div className="order-item-wide"><span>Options</span><strong>{Object.entries(item.attributes||{}).map(([k,v])=>k+': '+v).join(' · ')||'—'}</strong></div>
        <div className="order-item-wide"><span>Artwork</span><strong>{item.artworkAssets?.length?item.artworkAssets.map(asset=>asset.name||asset.printArea||'Artwork').join(' · '):'No stored artwork reference'}</strong></div>
      </article>)}</div>
    </section>

    {!isQuote&&providerOrder?.shipments?.length>0&&<section className="order-section">
      <div className="order-section-head"><div><span>DELIVERY</span><h2 className="govuk-heading-m">Shipments & tracking</h2></div></div>
      <div className="order-shipment-grid">{providerOrder.shipments.map((shipment,index)=><article key={shipment.id||index}><strong>{shipment.id||'Shipment '+(index+1)}</strong><Status value={shipment.status||'unknown'}/><span>{shipment.carrier?.name||'Carrier pending'}{shipment.carrier?.service?' · '+shipment.carrier.service:''}</span><small>{shipment.tracking?.number||'Tracking number not yet supplied'}</small>{shipment.tracking?.url&&<a href={shipment.tracking.url} target="_blank" rel="noreferrer">Open tracking <ExternalLink size={12}/></a>}</article>)}</div>
    </section>}

    {!isQuote&&record.cj_order_id&&<section className="order-section">
      <div className="order-section-head"><div><span>CJ · EVERYDAY</span><h2 className="govuk-heading-m">CJ fulfilment & tracking</h2></div></div>
      <div className="order-shipment-grid"><article>
        <strong>{record.cj_order_id}</strong>
        <Status value={record.cj_status||cjOrder?.status||'processing'}/>
        <span>{cjOrder?.trackingProvider||cjOrder?.logisticName||'Carrier pending'}{cjOrder?.logisticName&&cjOrder?.trackingProvider?' · '+cjOrder.logisticName:''}</span>
        <small>{cjOrder?.trackingNumber||'Tracking number not yet supplied'}</small>
        {cjOrder?.trackingUrl&&<a href={cjOrder.trackingUrl} target="_blank" rel="noreferrer">Open tracking <ExternalLink size={12}/></a>}
      </article></div>
    </section>}

    <div className="order-detail-grid order-support-grid">
      <Card title="Customer support">
        <div className="order-support-actions">
          {customerId&&<button className="button secondary govuk-button govuk-button--secondary" onClick={()=>navigate('/customers/'+encodeURIComponent(customerId)+'#cases')}><CircleHelp size={15}/>Cases & support</button>}
          {customerId&&<button className="button secondary govuk-button govuk-button--secondary" onClick={()=>navigate('/customers/'+encodeURIComponent(customerId)+'#billing')}><RotateCcw size={15}/>Billing / refunds</button>}
          {customerEmail&&<a className="button secondary govuk-button govuk-button--secondary" href={'mailto:'+customerEmail}><Mail size={15}/>Email customer</a>}
        </div>
      </Card>
      <Card title="Technical references">
        <DetailList>
          <Detail label="CRM record"><span className="order-copy-value">{record.id}<button onClick={()=>copy(record.id)} aria-label="Copy CRM record"><Copy size={12}/></button></span></Detail>
          <Detail label="Quote">{record.quote_reference||record.quote_id||'—'}</Detail>
          {!isQuote&&<Detail label="Prodigi">{record.prodigi_order_id||'—'}</Detail>}
          {!isQuote&&record.cj_order_id&&<Detail label="Legacy CJ">{record.cj_order_id}</Detail>}
          <Detail label="Payment provider">{isPayPalPayment?'PayPal':'Stripe'}</Detail>
          {isPayPalPayment
            ? <>
                <Detail label="PayPal order">{record.paypal_order_id||'—'}</Detail>
                {!isQuote&&<Detail label="PayPal capture">{record.paypal_capture_id||'—'}</Detail>}
              </>
            : <>
                <Detail label="Stripe Checkout">{record.stripe_checkout_session_id||'—'}</Detail>
                {!isQuote&&<Detail label="Stripe invoice">{record.stripe_invoice_id||'—'}</Detail>}
              </>}
        </DetailList>
      </Card>
    </div>

    <section className="order-section">
      <div className="order-section-head"><div><span>AUDIT</span><h2 className="govuk-heading-m">Order history</h2></div><strong>{events.length} events</strong></div>
      {events.length?<div className="order-history">{events.map(event=><div key={event.id}><span className="order-history-dot"/><div><strong>{event.event_type}</strong><small>{event.detail?.actorName||event.detail?.provider||event.detail?.message||''}</small></div><time>{dateTime(event.occurred_at)}</time></div>)}</div>:<div className="empty-inline operational-empty">No history has been recorded.</div>}
    </section>

    {data?.provider?.warnings?.length?<div className="operation-notice error">{data.provider.warnings.join(' · ')}</div>:null}
  </>;
}
